/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Input, Row } from "reactstrap";
import NumberFormat from "react-number-format";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import { confirmAlert } from "react-confirm-alert";
import _ from "lodash";
import { AttachmentManager, CustomerTypeahead, ItemTypeahead, ServiceThreadSpinner } from "../components";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, helpers, UserContext } from "../utils";
import axios from "axios";
import classNames from "classnames";

const bigLink = {
   color: "#F26723",
   fontSize: "1.5rem",
   textDecoration: "underline",
};

export default function CreateNewSampleItem(props) {
   const navigate = useNavigate();
   const { currentUser } = useContext(UserContext);
   const { routeId } = useParams();
   const cancelToken = axios.CancelToken;
   const source = cancelToken.source();
   const [errorList, setErrorList] = useState({
      abcCode: false,
      currentCost: false,
      customer: false,
      customerItem: false,
      customerItemDesc: false,
      itemDescription: false,
      listPrice: false,
      minOrderQty: false,
      stdCost: false,
      stdNetWeight: false,
      tareWeight: false,
      unitsPerSku: false,
   });
   const [miscOptions, setMiscOptions] = useState({
      copySpec: false,
      copyMachineSetup: false,
      wastePercent: 0,
   });
   const [refData, setRefData] = useState({});
   const [referenceItem, setReferenceItem] = useState(null);
   const [sampleEditCopy, setSampleEditCopy] = useState(null);
   const [sampleRequest, setSampleRequest] = useState({});
   const [working, setWorking] = useState(false);

   useEffect(() => {
      Promise.all([getItemClasses(), getSiteList(), getThreadColors()])
         .then((arrayResults) => {
            let aggResults = {};
            _.each(arrayResults, (x) => _.assign(aggResults, x));
            const { ItemClasses, SiteList, ThreadColors } = aggResults;
            setRefData({
               itemClasses: ItemClasses,
               siteList: SiteList,
               threadColors: ThreadColors,
            });
         })
         .catch(api.catchHandler);
   }, []);

   useEffect(() => {
      if (_.isNil(routeId) || routeId === "0") {
         const currentDate = new Date();
         setSampleRequest({
            id: 0,
            requestBy: currentUser.fullName,
            requestDate: dateHelpers.formatDateForServer(currentDate),
            dueDate: dateHelpers.formatDateForServer(new Date(currentDate.setDate(currentDate.getDate() + 3))),
            isActive: true,
         });
      } else {
         setWorking(true);
         const urlTokens = routeId.split("-");
         const requestId = urlTokens?.length > 1 ? urlTokens[1] : routeId;
         api
            .post("Item/GetSampleItemSearch", { id: requestId })
            .then((r) => {
               const srDto = r.data.requests[0];
               if (r.data.success && srDto) {
                  const refDto = r.data.refItem;
                  setSampleRequest(srDto);
                  if (_.isNil(srDto.itemClass) || srDto.itemClass.length === 0) {
                     if (!_.isNil(refDto)) {
                        srDto.currentCost = refDto.currentCost;
                        srDto.standardCost = refDto.standardCost;
                        srDto.listPrice = refDto.listPrice;
                        srDto.shippingClass = refDto.shippingClass;
                        srDto.tareWeight = refDto.tareWeight;
                        srDto.stdNetWeight = refDto.standardNetWeight;
                        srDto.unitsPerSku = refDto.unitsPerSku;
                        srDto.abcCode = refDto.abcCode;
                     }
                     setSampleEditCopy({ ...srDto });
                  }
                  setReferenceItem(refDto);
               } else {
                  toast.error("Could not load request id " + requestId);
                  console.error(r.data.message);
               }
            })
            .catch(api.catchHandler)
            .finally(() => setWorking(false));
      }

      return () => {
         source.cancel("axios request cancelled");
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [routeId]);

   function beginSampleRequest() {
      setWorking(true);
      const payload = { ...sampleRequest };
      if (!_.isNil(sampleRequest.referenceItem))
         payload.referenceItem = sampleRequest.referenceItem.value;
      api
         .post(`Item/InsertSampleItemRequest`, payload)
         .then((r) => {
            if (r.data.success) {
               const resultMessage = "Your sample item request has been created successfully!";
               toast.success(resultMessage);
               navigate(constants.ENDPOINTS.createItemCode);
               // navigate(0);
            } else {
               toast.error(r.data.message);
               setWorking(false);
            }
         })
         .catch((err) => {
            toast.error("Error initiating sample item request");
            console.error(err);
            setWorking(false);
         });
   }

   function getItemClasses() {
      return api
         .fetch("Reference/GetItemClasses")
         .then((response) => {
            if (response.data && response.data.success) {
               return {
                  ItemClasses: _.chain(response.data.itemClasses)
                     .map((opt) => ({
                        ...opt,
                        value: opt.itemClassCode,
                        label: opt.itemClassWithDesc,
                     }))
                     .sortBy((opt) => opt.label)
                     .value(),
               };
            } else {
               toast.error("Could not find item class list");
            }
         })
         .catch(api.catchHandler);
   }

   function getNextStep() {
      let nextStep = "";
      let nextUrl = "";
      const reviewItemSelect = JSON.stringify({
         value: sampleRequest.itemNumber,
         label: sampleRequest.itemWithDescription,
      });
      if (!_.isEmpty(_.trim(sampleRequest.machineSetupBy))) {
         nextStep = "BOM Edit";
         nextUrl = `/bom-components/search?bomComponent=${reviewItemSelect}`;
      } else if (!_.isEmpty(_.trim(sampleRequest.specSetupBy))) {
         nextStep = "Machine Setup";
         nextUrl = `/machine-setups/${reviewItemSelect}`;
      } else if (!_.isEmpty(_.trim(sampleRequest.itemSetupBy))) {
         nextStep = "Specification Edit";
         nextUrl = `/quality-specifications/${reviewItemSelect}`;
      }
      return (
         <a href={nextUrl} target="_blank" rel="noreferrer" className="styledAnchor">
            Click here to go to the {nextStep} Screen
         </a>
      );
   }

   function getSampleRequestValue(key, defaultValue = "") {
      const value = _.isNil(sampleEditCopy)
         ? sampleRequest[key]
         : sampleEditCopy[key];
      return _.isNil(value) || value === "" ? defaultValue : value;
   }

   function getSiteList() {
      return api
         .fetch("Reference/GetSiteList")
         .then((response) => {
            if (response.data && response.data.success) {
               return {
                  SiteList: _.map(response.data.sites, (c) =>
                     Object.assign(c, {
                        label: c.siteWithDescription,
                        value: c.siteId,
                     })
                  ),
               };
            } else {
               toast.error("Could not find site list");
            }
         })
         .catch(api.catchHandler);
   }

   function getThreadColors() {
      return api
         .fetch("Reference/GetThreadColors")
         .then((response) => {
            if (response.data && response.data.success) {
               return {
                  ThreadColors: _.map(response.data.threadColors, (c) =>
                     Object.assign(c, { label: c.name, value: c.id })
                  ),
               };
            } else {
               toast.error("Could not find thread colors");
            }
         })
         .catch(api.catchHandler);
   }

   function handleMiscOptionChange(key, value) {
      const optionsUpdate = { ...miscOptions };
      optionsUpdate[key] = value;
      setMiscOptions(optionsUpdate);
   }

   function handlePromote() {
      confirmAlert({
         title: "Promoting Sample",
         message: "This will create a sales inventory item in Great Plains and remove this sample item.  Continue?",
         buttons: [
            { label: "Yes", onClick: handlePromoteFinal, },
            { label: "No", },
         ],
      });
   }

   function handlePromoteFinal() {
      setWorking(true);
      api
         .fetch(`Item/PromoteSampleItem/${sampleRequest.itemNumber}`)
         .then((r) => {
            if (r.data.success) {
               navigate(constants.ENDPOINTS.createItemCode);
            } else {
               toast.error(r.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setWorking(false));
   }

   function handleSampleRequestEdit(key, value) {
      const sampleUpdate = { ...sampleEditCopy };
      if (key === "itemClass" && _.includes(constants.FINISHED_GOOD_CLASS_CODES, value)) {
         sampleUpdate.shippingClass = "A";
      }
      sampleUpdate[key] = value;
      setSampleEditCopy(sampleUpdate);
   }

   function handleSampleRequestSave() {
      const payload = { ...miscOptions, sampleItemRequestDto: sampleEditCopy };
      const [Validity, Messages] = validatePayload(payload, sampleRequest.customerId);
      if (Validity === false) {
         toast.warning(Messages.join(", "));
         return;
      }
      if (!_.isNil(payload.sampleItemRequestDto.customer))
         payload.sampleItemRequestDto.customerId = payload.sampleItemRequestDto.customer.value;
      if (!_.isNil(payload.sampleItemRequestDto.color))
         payload.sampleItemRequestDto.color = payload.sampleItemRequestDto.color.label;
      if (_.isNil(payload.sampleItemRequestDto.color))
         payload.sampleItemRequestDto.color = _.find(
            refData.threadColors,
            (tc) => tc.label === getSampleRequestValue("color", "NATURAL")
         )?.label;
      // A blank site is stored as "" (not null), which the Site dropdown still *displays* as PLT
      // via getSampleRequestValue's fallback — so an un-touched form persists an empty SiteId that
      // later blocks promotion (GP taItemSite needs a location). _.isNil misses "", so use a falsy
      // check to catch it and default the stored value to PLT, keeping storage in sync with display.
      if (!payload.sampleItemRequestDto.siteId)
         payload.sampleItemRequestDto.siteId = _.find(
            refData.siteList,
            (tc) => tc.value === getSampleRequestValue("siteId", "PLT")
         )?.value;
      payload.sampleItemRequestDto.tareWeight = _.toString(payload.sampleItemRequestDto.tareWeight);
      payload.sampleItemRequestDto.stdNetWeight = _.toString(payload.sampleItemRequestDto.stdNetWeight);
      payload.sampleItemRequestDto.unitsPerSku = _.toString(payload.sampleItemRequestDto.unitsPerSku);
      payload.sampleItemRequestDto.minOrderQty = _.toString(payload.sampleItemRequestDto.minOrderQty);
      setWorking(true);
      api
         .postS("Item/SaveSampleItemRequest", payload, source.token)
         .then((r) =>
            r.data.success
               ? navigate(constants.ENDPOINTS.createItemCode)
               : toast.error("Sample Item was not saved successfully.")
         )
         .catch(api.catchHandler)
         .finally(() => setWorking(false));
   }

   function replacer(key, value) {
      // Encode # symbol in item string for inventory dashboard link
      if (typeof value === 'string')
         return value.replace(/#/g, '%23');

      return value;
   }

   function toggleSampleRequestEdit() {
      setSampleEditCopy(
         _.isNil(sampleEditCopy)
            ? _.assign({}, sampleRequest)
            : null
      );
   }

   function validatePayload(payload, withCustomer = false) {
      let errors = [];
      payload.sampleItemRequestDto.tareWeight = _.toString(payload.sampleItemRequestDto.tareWeight);
      payload.sampleItemRequestDto.stdNetWeight = _.toString(payload.sampleItemRequestDto.stdNetWeight);
      payload.sampleItemRequestDto.unitsPerSku = _.toString(payload.sampleItemRequestDto.unitsPerSku);
      payload.sampleItemRequestDto.minOrderQty = _.toString(payload.sampleItemRequestDto.minOrderQty);
      const possibleErrors = _.cloneDeep(errorList);
      let ErrorListReset = _.map(possibleErrors, (x) => (x = false));
      if (withCustomer) {
         if (_.isNil(payload.sampleItemRequestDto.customer)) {
            errors.push("Customer is a required field!");
            ErrorListReset.customer = true;
         }

         if (_.isEmpty(payload.sampleItemRequestDto.customerItemNumber)) {
            errors.push("Customer Item is a required field!");
            ErrorListReset.customerItem = true;
         }

         if (_.isEmpty(payload.sampleItemRequestDto.customerItemDescription)) {
            errors.push("Customer Item Description is a required field!");
            ErrorListReset.customerItemDesc = true;
         }
      }
      if (_.isEmpty(payload.sampleItemRequestDto.itemDescription)) {
         errors.push("Item Description is a required field!");
         ErrorListReset.itemDescription = true;
      }
      if (_.isUndefined(payload.sampleItemRequestDto.standardCost) ||
         payload.sampleItemRequestDto.standardCost <= 0
      ) {
         ErrorListReset.stdCost = true;
         if (payload.sampleItemRequestDto.standardCost <= 0) {
            errors.push("Standard Cost must be in format 9999.99");
         } else {
            errors.push("Standard Cost is a required field!");
         }
      }
      if (_.isUndefined(payload.sampleItemRequestDto.currentCost) ||
         payload.sampleItemRequestDto.currentCost <= 0
      ) {
         ErrorListReset.currentCost = true;
         if (payload.sampleItemRequestDto.currentCost <= 0) {
            errors.push("Current Cost must be in format 9999.99");
         } else {
            errors.push("Current Cost is a required field!");
         }
      }
      if (_.isUndefined(payload.sampleItemRequestDto.listPrice) ||
         payload.sampleItemRequestDto.listPrice <= 0
      ) {
         ErrorListReset.listPrice = true;
         if (payload.sampleItemRequestDto.listPrice <= 0) {
            errors.push("List Price must be in format 9999.99");
         } else {
            errors.push("List Price is a required field!");
         }
      }
      if (_.isEmpty(payload.sampleItemRequestDto.tareWeight)) {
         errors.push("Tare Weight is a required field!");
         ErrorListReset.tareWeight = true;
      }
      if (_.isEmpty(payload.sampleItemRequestDto.stdNetWeight)) {
         errors.push("Std Net Weight is a required field!");
         ErrorListReset.stdNetWeight = true;
      }
      if (_.isEmpty(payload.sampleItemRequestDto.unitsPerSku)) {
         errors.push("Units Per Sku is a required field!");
         ErrorListReset.unitsPerSku = true;
      }
      if (_.isEmpty(payload.sampleItemRequestDto.minOrderQty)) {
         errors.push("Minimum Order Qty is a required field!");
         ErrorListReset.minOrderQty = true;
      }
      if (!payload.sampleItemRequestDto.abcCode) {
         errors.push("abc Code is required");
         ErrorListReset.abcCode = true;
      }
      if (errors.length) {
         setErrorList(ErrorListReset);
         return [false, errors];
      }
      return [true, errors];
   }

   return (
      <DesktopContainer screenName="Sample Item">
         <ServiceThreadSpinner on={working}>
            <div className="filter-header mb-2">
               <Row className="justify-content-center text-white">
                  <Col xxl={9}>
                     <div id="header">
                        <Row>
                           <Col xxl={4} className="text-end">
                              <h5>Request Id:</h5>
                           </Col>
                           <Col xxl={6}>
                              <h5>{sampleRequest.id ? `SR-${sampleRequest.id}` : "Determined upon save"}</h5>
                           </Col>
                        </Row>
                        <Row>
                           <Col xxl={4} className="text-end">
                              <h5>Requested By:</h5>
                           </Col>
                           <Col xxl={6}>
                              <h5>
                                 {sampleRequest.requestBy} on{" "}
                                 {dateHelpers.toLongDateString(sampleRequest.requestDate)}
                              </h5>
                           </Col>
                        </Row>
                        <Row>
                           <Col xxl={4} className="text-end">
                              <h5>Due Date:</h5>
                           </Col>
                           <Col xxl={6}>
                              <h5>{sampleRequest.id !== 0 && dateHelpers.toLongDateString(sampleRequest.dueDate)}</h5>
                           </Col>
                        </Row>
                        <div id="header-ref-link" className={classNames({ "d-none": sampleRequest.id === 0 })} >
                           <Row>
                              <Col xxl={4} className="text-end">
                                 <h5>Reference Item:</h5>
                              </Col>
                              <Col xxl={6}>
                                 <a rel="noreferrer"
                                    target="_blank"
                                    style={bigLink}
                                    href={
                                       _.isNil(sampleRequest.referenceItem)
                                          // eslint-disable-next-line no-script-url
                                          ? "javascript:void(0);"
                                          : `/inventoryDashboard/${JSON.stringify({
                                             value: sampleRequest.referenceItem,
                                             label: sampleRequest.referenceItemString,
                                          }, replacer)}`
                                    }
                                 >
                                    {_.isNil(sampleRequest.referenceItem)
                                       ? "Reference Item was not selected"
                                       : sampleRequest.referenceItemString}
                                 </a>
                              </Col>
                           </Row>
                           <Row>
                              <Col xxl={{ offset: 0, size: 4 }} className="text-end">
                                 <h6>{sampleRequest.itemComment}</h6>
                              </Col>
                           </Row>
                        </div>
                        <div
                           id="customer-info"
                           className={classNames({
                              "d-none":
                                 _.isNil(sampleRequest.customerId) ||
                                 sampleRequest.customerId?.length === 0,
                           })}
                        >
                           <Row>
                              <Col xxl={4} className="text-end">
                                 <h5>Customer:</h5>
                              </Col>
                              <Col xxl={6}>
                                 <h5>{sampleRequest.customerIdName}</h5>
                              </Col>
                           </Row>
                           <Row>
                              <Col xxl={4} className="text-end">
                                 <h5>Customer Item:</h5>
                              </Col>
                              <Col xxl={6}>
                                 <h5>{sampleRequest.customerItemWithDescription}</h5>
                              </Col>
                           </Row>
                        </div>
                     </div>
                  </Col>
               </Row>

               <hr />
               <div id="new-request" className={classNames({ "d-none": sampleRequest.id > 0 })} >
                  <Row>
                     <Col xxl={{ offset: 2, size: 8 }}>
                        <Row>
                           <Col xxl={12} className="pt-2 text-white">
                              <p className="my-1">
                                 Use the Reference Item field only for existing items you
                                 wish to use as base information for the new item.
                              </p>
                              <p>
                                 Use the Item Comment field to enter information about the
                                 new item including any item code suggestions.
                              </p>
                           </Col>
                        </Row>
                        <Row className="py-2">
                           <Col xxl="3" className="h5 text-white" children="Reference Item" />
                           <Col xxl={9}>
                              <ItemTypeahead
                                 value={sampleRequest.referenceItem}
                                 placeholder="Select Select an EXISTING sample/item number or leave blank"
                                 includeSampleItems
                                 onChange={(opt) =>
                                    setSampleRequest({ ...sampleRequest, referenceItem: opt, })
                                 }
                                 isClearable
                              />
                           </Col>
                        </Row>
                        <Row className="pb-2 text-white">
                           <Col xxl={3} className="h5">
                              Item Comment
                              {!sampleRequest.itemComment ? helpers.requiredStar() : null}
                           </Col>
                           <Col xxl={9}>
                              <Input
                                 className="serviceThreadTextArea"
                                 type="textarea"
                                 value={sampleRequest.itemComment}
                                 maxLength={140}
                                 onChange={(e) =>
                                    setSampleRequest({ ...sampleRequest, itemComment: e.target.value, })
                                 }
                              />
                           </Col>
                        </Row>
                        <Row className="pb-2 text-white">
                           <Col xxl={3} className="h5">
                              Due Date
                              {!sampleRequest.dueDate ? helpers.requiredStar() : null}
                           </Col>
                           <Col xxl={9}>
                              <Input
                                 type="date"
                                 value={sampleRequest.dueDate ?? ""}
                                 onChange={(e) =>
                                    setSampleRequest({ ...sampleRequest, dueDate: e.target.value, })
                                 }
                              />
                           </Col>
                        </Row>
                        <Row className="mb-2">
                           <Col>
                              <Button
                                 className="shredButton float-end"
                                 onClick={beginSampleRequest}
                                 disabled={
                                    working ||
                                    _.isNil(sampleRequest.itemComment) ||
                                    sampleRequest.dueDate === 0
                                 }
                              >
                                 Start Request
                              </Button>
                           </Col>
                        </Row>
                     </Col>
                  </Row>
               </div>
               <div id="in-process-request" className={classNames({ "d-none": !sampleRequest.id })} >
                  <div id="review-request" className={classNames({ "d-none": !_.isNil(sampleEditCopy) })} >
                     {_.isNil(sampleRequest) ? (
                        <Row>
                           <Col className="text-center">
                              <span className="fw-bold text-danger">
                                 ERROR: Unable to find the sample item request for the ID provided.
                              </span>
                           </Col>
                        </Row>
                     ) : (
                        <>
                           <Row className="mb-2">
                              <Col xxl={4} className="text-end text-white">
                                 <h5>Item:</h5>
                              </Col>
                              <Col xxl={2} className="text-white">
                                 {sampleRequest.itemWithDescription}
                              </Col>
                              <Col xxl={2}>
                                 <Button
                                    className={classNames("shredButton", {"d-none": !sampleRequest.isActive || !currentUser?.isAdmin,})}
                                    onClick={toggleSampleRequestEdit}
                                 >
                                    Edit Item
                                 </Button>
                              </Col>
                           </Row>
                           <Row className={classNames("mb-2", {"d-none": _.isNil(sampleRequest.itemSetupBy)})} >
                              <Col xxl={4} className="text-end text-white">
                                 <h5>Item Setup:</h5>
                              </Col>
                              <Col xxl={2} className="text-white">
                                 <span>Date:</span>{" "}
                                 {dateHelpers.toMDYDateString(sampleRequest.itemSetupDate)}
                              </Col>
                              <Col xxl={2} className="text-white">
                                 <span>Completed By:</span> {sampleRequest.itemSetupBy}
                              </Col>
                           </Row>
                           <Row
                              className={classNames("mb-2 text-white", {
                                 "d-none": _.isNil(sampleRequest.specSetupBy),
                              })}
                           >
                              <Col xxl={4} className="text-end">
                                 <h5>Specification Setup:</h5>
                              </Col>
                              <Col xxl={2}>
                                 <span>Date:</span>{" "}
                                 {dateHelpers.toMDYDateString(sampleRequest.specSetupDate)}
                              </Col>
                              <Col xxl={2}>
                                 <span>Completed By:</span> {sampleRequest.specSetupBy}
                              </Col>
                           </Row>
                           <Row className={classNames("mb-2 text-white", {"d-none": _.isNil(sampleRequest.machineSetupBy)})} >
                              <Col xxl={4} className="text-end">
                                 <h5>Machine Setup(s):</h5>
                              </Col>
                              <Col xxl={2}>
                                 <span>Date:</span>{" "}
                                 {dateHelpers.toMDYDateString(sampleRequest.machineSetupDate)}
                              </Col>
                              <Col xxl={2}>
                                 <span>Completed By:</span> {sampleRequest.machineSetupBy}
                              </Col>
                           </Row>
                           <Row className={classNames("mb-2 text-white", {"d-none": _.isNil(sampleRequest.bomSetupBy)})} >
                              <Col xxl={4} className="text-end">
                                 <h5>BOM Setup:</h5>
                              </Col>
                              <Col xxl={2}>
                                 <span>Date:</span>{" "}
                                 {dateHelpers.toMDYDateString(sampleRequest.bomSetupDate)}
                              </Col>
                              <Col xxl={2}>
                                 <span>Completed By:</span> {sampleRequest.bomSetupBy}
                              </Col>
                           </Row>
                           {(!_.isEmpty(_.trim(sampleRequest.bomSetupBy)) &&
                              !_.isEmpty(_.trim(sampleRequest.machineSetupBy))) ||
                              (_.upperCase(sampleRequest.itemClass) === "DSHIP" &&
                                 !_.isEmpty(_.trim(sampleRequest.specSetupBy))) ? (
                              <Row className="text-white">
                                 <Col xxl={{ offset: 0, size: 4 }}>
                                    <h5 className="float-end">Promote Sample</h5>
                                 </Col>
                                 <Col className="text-center">
                                    <Button className="shredButton float-start" onClick={handlePromote} >
                                       Promote
                                    </Button>
                                 </Col>
                              </Row>
                           ) : (
                              <>
                                 <Row>
                                    <Col className="text-center">
                                       <span className="fw-bold p-1 text-brightRed bg-white">
                                          This item is not available for promotion. Not all
                                          steps have been completed.
                                       </span>
                                    </Col>
                                 </Row>
                                 <Row>
                                    <Col className="text-center">{getNextStep()}</Col>
                                 </Row>
                              </>
                           )}
                        </>
                     )}
                  </div>
                  <Row id="edit-request" className={classNames({ "d-none": _.isNil(sampleEditCopy) })} >
                     {currentUser?.isAdmin ||
                        currentUser?.isPowerUser ||
                        currentUser?.isEngineerEdit ? (
                        <>
                           <Row className="mb-2">
                              <Col xxl={2} className="text-end text-white">
                                 Item Class
                              </Col>
                              <Col xxl={9} className="text-black">
                                 <Select
                                    options={refData.itemClasses}
                                    value={_.find(
                                       refData.itemClasses,
                                       (ic) => ic.value === getSampleRequestValue("itemClass", null)
                                    )}
                                    onChange={(opt) => handleSampleRequestEdit("itemClass", opt.value)}
                                    menuPlacement="auto"
                                 />
                              </Col>
                           </Row>
                           {_.isNil(referenceItem) ? null : (
                              <Row className="mb-2">
                                 <Col xxl={{ offset: 2, size: 8 }} className="text-white">
                                    <Input
                                       type="checkbox"
                                       className="me-3 ms-2"
                                       value={miscOptions.copySpec}
                                       onChange={(e) => handleMiscOptionChange("copySpec", e.target.checked)}
                                    />
                                    Pre-populate the new specification with a copy of the
                                    Reference Item Values?
                                 </Col>
                              </Row>
                           )}
                           <Row
                              className={classNames("mb-2", {
                                 "d-none":
                                    getSampleRequestValue("itemClass") === "DSHIP" ||
                                    _.isNil(referenceItem),
                              })}
                           >
                              <Col xxl={{ offset: 2, size: 8 }} className="text-white">
                                 <Input
                                    type="checkbox"
                                    className="me-3 ms-2"
                                    value={miscOptions.copyMachineSetup}
                                    onChange={(e) => handleMiscOptionChange("copyMachineSetup", e.target.checked)}
                                 />
                                 Pre-populate machine setups with a copy of the Reference
                                 Item setup(s)?
                              </Col>
                           </Row>
                           <div id="edit-fields"
                              className={classNames("text-white", {"d-none": _.isNil(getSampleRequestValue("itemClass", null)),})}
                           >
                              <Row className="mb-2">
                                 <Col xxl={2} className="text-end">
                                    Item
                                 </Col>
                                 <Col xxl={9}>
                                    {!_.isNil(sampleRequest.itemNumber) &&
                                       sampleRequest.itemNumber?.length > 0
                                       ? sampleRequest.itemNumber
                                       : "To be determined by class selected and provided upon saving the new item"}
                                 </Col>
                              </Row>
                              <Row className="mb-2">
                                 <Col xxl={2} className="text-end">
                                    Item Description
                                    {!sampleEditCopy?.itemDescription?.length
                                       ? helpers.requiredStar()
                                       : null}
                                 </Col>
                                 <Col xxl={9}>
                                    <Input
                                       value={getSampleRequestValue("itemDescription")}
                                       onChange={(e) => handleSampleRequestEdit("itemDescription", e.target.value)}
                                    />
                                 </Col>
                              </Row>
                              <Row className="mb-2">
                                 <Col xxl={2} className="text-end">
                                    Color
                                 </Col>
                                 <Col xxl={9} className="text-black">
                                    <Select
                                       options={refData.threadColors}
                                       // value={getSampleRequestValue("color", null)}
                                       value={_.find(
                                          refData.threadColors,
                                          (tc) => tc.label === getSampleRequestValue("color", "NATURAL")
                                       )}
                                       onChange={(opt) => handleSampleRequestEdit("color", opt)}
                                       menuPlacement="auto"
                                       isClearable={false}
                                    />
                                 </Col>
                              </Row>
                              <div
                                 id="customer-info"
                                 className={classNames({
                                    "d-none": !_.includes(
                                       constants.FINISHED_GOOD_CLASS_CODES,
                                       getSampleRequestValue("itemClass")
                                    ),
                                 })}
                              >
                                 <Row className="mb-2">
                                    <Col xxl={2} className="text-end">
                                       Customer
                                       {!sampleEditCopy?.customer?.length
                                          ? helpers.requiredStar()
                                          : null}
                                    </Col>
                                    <Col xxl={9} className="text-black">
                                       <CustomerTypeahead
                                          value={getSampleRequestValue("customer")}
                                          onChange={(opt) => handleSampleRequestEdit("customer", opt)}
                                       />
                                    </Col>
                                 </Row>
                                 <Row className="mb-2">
                                    <Col xxl={2} className="text-end">
                                       Customer Item
                                       {!sampleEditCopy?.customerItem?.length
                                          ? helpers.requiredStar()
                                          : null}
                                    </Col>
                                    <Col xxl={9}>
                                       <Input
                                          value={getSampleRequestValue("customerItemNumber")}
                                          onChange={(e) => handleSampleRequestEdit("customerItemNumber", e.target.value)}
                                       />
                                    </Col>
                                 </Row>
                                 <Row className="mb-2">
                                    <Col xxl={2} className="text-end">
                                       Customer Item Description
                                       {!sampleEditCopy?.customerItemDesc?.length
                                          ? helpers.requiredStar()
                                          : null}
                                    </Col>
                                    <Col xxl={9}>
                                       <Input
                                          value={getSampleRequestValue("customerItemDescription")}
                                          onChange={(e) => handleSampleRequestEdit("customerItemDescription", e.target.value)}
                                       />
                                    </Col>
                                 </Row>
                              </div>
                              <Row className="mb-2">
                                 <Col xxl={2} className="text-end">
                                    Standard Cost
                                    {!sampleEditCopy?.standardCost > 0
                                       ? helpers.requiredStar()
                                       : null}
                                 </Col>
                                 <Col xxl={9}>
                                    <NumberFormat
                                       className="form-control"
                                       value={getSampleRequestValue("standardCost", 0.0).toFixed(2)}
                                       onValueChange={({ floatValue }) => handleSampleRequestEdit("standardCost", floatValue)}
                                       allowNegative={false}
                                    />
                                 </Col>
                              </Row>
                              <Row className="mb-2">
                                 <Col xxl={2} className="text-end">
                                    Current Cost
                                    {!sampleEditCopy?.currentCost
                                       ? helpers.requiredStar()
                                       : null}
                                 </Col>
                                 <Col xxl={9}>
                                    <NumberFormat
                                       className="form-control"
                                       value={getSampleRequestValue("currentCost", 0.0).toFixed(2)}
                                       onValueChange={({ floatValue }) => handleSampleRequestEdit("currentCost", floatValue)}
                                       allowNegative={false}
                                    />
                                 </Col>
                              </Row>
                              <Row className="mb-2">
                                 <Col xxl={2} className="text-end">
                                    List Price
                                    {!sampleEditCopy?.listPrice
                                       ? helpers.requiredStar()
                                       : null}
                                 </Col>
                                 <Col xxl={9}>
                                    <NumberFormat
                                       className="form-control"
                                       value={getSampleRequestValue("listPrice")}
                                       onValueChange={({ floatValue }) => handleSampleRequestEdit("listPrice", floatValue)}
                                       allowNegative={false}
                                    />
                                 </Col>
                              </Row>
                              <Row className="mb-2">
                                 <Col xxl={2} className="text-end">
                                    Shipping Class
                                 </Col>
                                 <Col xxl={9}>
                                    <Input
                                       type="radio"
                                       name="shippingClass"
                                       className="me-1"
                                       checked={ getSampleRequestValue("shippingClass") === "A" }
                                       onChange={() => handleSampleRequestEdit("shippingClass", "A") }
                                    />
                                    A
                                    <Input
                                       type="radio"
                                       name="shippingClass"
                                       className="ms-4 me-1"
                                       checked={ getSampleRequestValue("shippingClass") === "B" }
                                       onChange={() => handleSampleRequestEdit("shippingClass", "B")}
                                    />
                                    B
                                    <Input
                                       type="radio"
                                       name="shippingClass"
                                       className="ms-4 me-1"
                                       checked={ getSampleRequestValue("shippingClass") === "C" }
                                       onChange={() => handleSampleRequestEdit("shippingClass", "C")}
                                    />
                                    C
                                 </Col>
                              </Row>
                              <Row className="mb-2">
                                 <Col xxl={2} className="text-end">
                                    Tare Weight
                                    {!sampleEditCopy?.tareWeight > 0
                                       ? helpers.requiredStar()
                                       : null}
                                 </Col>
                                 <Col xxl={9}>
                                    <NumberFormat
                                       className="form-control"
                                       value={getSampleRequestValue("tareWeight")}
                                       onValueChange={({ floatValue }) => handleSampleRequestEdit("tareWeight", floatValue)}
                                       allowNegative={false}
                                    />
                                 </Col>
                              </Row>
                              <Row className="mb-2">
                                 <Col xxl={2} className="text-end">
                                    Std Net Weight
                                    {!sampleEditCopy?.stdNetWeight > 0
                                       ? helpers.requiredStar()
                                       : null}
                                 </Col>
                                 <Col xxl={9}>
                                    <NumberFormat
                                       className="form-control"
                                       value={getSampleRequestValue("stdNetWeight")}
                                       onValueChange={({ floatValue }) => {
                                          if (floatValue) floatValue = parseInt(floatValue);
                                          if (typeof floatValue === "number") {
                                             handleSampleRequestEdit("stdNetWeight", floatValue);
                                          }
                                       }}
                                       allowNegative={false}
                                    />
                                 </Col>
                              </Row>
                              <Row className="mb-2">
                                 <Col xxl={2} className="text-end">
                                    Units Per Sku
                                    {!sampleEditCopy?.unitsPerSku > 0
                                       ? helpers.requiredStar()
                                       : null}
                                 </Col>
                                 <Col xxl={9}>
                                    <NumberFormat
                                       className="form-control"
                                       value={getSampleRequestValue("unitsPerSku")}
                                       onValueChange={({ floatValue }) => handleSampleRequestEdit("unitsPerSku", floatValue)}
                                       allowNegative={false}
                                    />
                                 </Col>
                              </Row>
                              <Row className="mb-2">
                                 <Col xxl={2} className="text-end">
                                    Minimum Order Qty
                                    {!sampleEditCopy?.minOrderQty > 0
                                       ? helpers.requiredStar()
                                       : null}
                                 </Col>
                                 <Col xxl={9}>
                                    <NumberFormat
                                       className="form-control"
                                       value={getSampleRequestValue("minOrderQty")}
                                       onValueChange={({ floatValue }) => handleSampleRequestEdit("minOrderQty", floatValue)}
                                       allowNegative={false}
                                    />
                                 </Col>
                              </Row>
                              <Row className="mb-2">
                                 <Col xxl={2} className="text-end">
                                    Site ID:
                                 </Col>
                                 <Col xxl={9} className="text-black">
                                    <Select
                                       options={refData.siteList}
                                       value={_.find(
                                          refData.siteList,
                                          (tc) => tc.value === getSampleRequestValue("siteId", "PLT")
                                       )}
                                       onChange={(opt) => handleSampleRequestEdit("siteId", opt.value)}
                                       menuPlacement="auto"
                                       isClearable={false}
                                    />
                                 </Col>
                              </Row>
                              <Row className="mb-2">
                                 <Col xxl={2} className="text-end">
                                    Bin Location
                                 </Col>
                                 <Col xxl={9}>
                                    <Input
                                       value={getSampleRequestValue("binLocation")}
                                       onChange={(e) => handleSampleRequestEdit("binLocation", e.target.value)}
                                    />
                                 </Col>
                              </Row>
                              <Row className="mb-2">
                                 <Col xxl={2} className="text-end">
                                    ABC Code
                                    {!sampleEditCopy?.abcCode
                                       ? helpers.requiredStar()
                                       : null}
                                 </Col>
                                 <Col xxl={9}>
                                    <Input
                                       type="radio"
                                       name="abcCode"
                                       className="me-1"
                                       checked={ getSampleRequestValue("abcCode") === constants.ABC_CODE.A.value }
                                       onChange={() => handleSampleRequestEdit("abcCode", constants.ABC_CODE.A.value)}
                                    />
                                    A
                                    <Input
                                       type="radio"
                                       name="abcCode"
                                       className="ms-4 me-1"
                                       checked={ getSampleRequestValue("abcCode") === constants.ABC_CODE.B.value }
                                       onChange={() => handleSampleRequestEdit("abcCode", constants.ABC_CODE.B.value)}
                                    />
                                    B
                                    <Input
                                       type="radio"
                                       name="abcCode"
                                       className="ms-4 me-1"
                                       checked={ getSampleRequestValue("abcCode") === constants.ABC_CODE.C.value }
                                       onChange={() => handleSampleRequestEdit("abcCode", constants.ABC_CODE.C.value)}
                                    />
                                    C
                                 </Col>
                              </Row>
                              <div
                                 id="waste"
                                 className={classNames({
                                    "d-none": _.includes(
                                       constants.FINISHED_GOOD_CLASS_CODES,
                                       getSampleRequestValue("itemClass")
                                    ),
                                 })}
                              >
                                 <Row id="waste" className="mb-2">
                                    <Col xxl={2} className="text-end">
                                       Waste
                                    </Col>
                                    <Col xxl={9}>
                                       <NumberFormat
                                          className="form-control"
                                          value={miscOptions.wastePercent}
                                          onValueChange={({ floatValue }) => {
                                             if (floatValue > 100) floatValue = 100;
                                             handleMiscOptionChange("wastePercent", floatValue);
                                          }}
                                          allowNegative={false}
                                       />
                                    </Col>
                                 </Row>
                              </div>
                              <Row className="px-3">
                                 <Col>
                                    <Button
                                       className="shredButton float-end"
                                       onClick={handleSampleRequestSave}
                                       disabled={working}
                                    >
                                       Create Item
                                    </Button>
                                 </Col>
                              </Row>
                           </div>
                        </>
                     ) : (
                        <p>Not an allowed sample item creator!</p>
                     )}
                  </Row>
               </div>
            </div>
            <Card className="dashboard-card">
               <CardBody>
                  <AttachmentManager
                     attachmentTypeId={constants.ATTACHMENT_TYPES.SampleItemRequest}
                     contextName={sampleRequest.displayId}
                     attachmentKey={sampleRequest.displayId}
                  />
               </CardBody>
            </Card>
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
